import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { listFlags, getFlagCounts } from './anomalyApi';

const styles = {
    page:    { padding: 24, background: '#f8fafc', minHeight: '100vh', fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif' },
    h1:      { fontSize: 22, fontWeight: 700, color: '#0f172a', margin: 0 },
    sub:     { fontSize: 13, color: '#64748b', marginTop: 4 },
    bar:     { display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0 8px', flexWrap: 'wrap' },
    chip:    { padding: '6px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: '#fff', border: '1px solid #e2e8f0', color: '#334155', cursor: 'pointer' },
    chipOn:  { background: '#0f172a', color: '#fff', borderColor: '#0f172a' },
    cards:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 },
    card:    { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '14px 16px' },
    cardLbl: { fontSize: 11, textTransform: 'uppercase', color: '#64748b', letterSpacing: '.04em' },
    cardVal: { fontSize: 26, fontWeight: 700, color: '#0f172a', marginTop: 4 },
    table:   { width: '100%', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, borderCollapse: 'separate', borderSpacing: 0, fontSize: 13 },
    th:      { textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.04em', background: '#f8fafc' },
    td:      { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', color: '#0f172a' },
    pill:    (sev) => ({
        display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
        background: sev === 'high' ? '#fee2e2' : sev === 'medium' ? '#fef3c7' : '#e0f2fe',
        color:      sev === 'high' ? '#991b1b' : sev === 'medium' ? '#92400e' : '#075985',
    }),
    link:    { color: '#2563eb', textDecoration: 'none', fontWeight: 600 },
    empty:   { padding: 32, textAlign: 'center', color: '#64748b' },
    err:     { padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', fontSize: 13 },
};

const SEVERITIES = ['all', 'high', 'medium', 'low'];
const STATUSES   = ['open', 'resolved', 'dismissed', 'escalated', 'all'];
const SOURCES    = ['all', 'pcard', 'fuelcard', 'timecard'];

export default function FlagsDashboard() {
    const [filters, setFilters] = useState({ severity: 'all', status: 'open', source_type: 'all' });
    const [rows, setRows] = useState([]);
    const [counts, setCounts] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        async function run() {
            setLoading(true);
            setError(null);
            try {
                const apiFilters = {
                    status:      filters.status      === 'all' ? null : filters.status,
                    severity:    filters.severity    === 'all' ? null : filters.severity,
                    source_type: filters.source_type === 'all' ? null : filters.source_type,
                    limit: 200,
                };
                const [list, c] = await Promise.all([listFlags(apiFilters), getFlagCounts()]);
                if (!cancelled) {
                    setRows(list.data || []);
                    setCounts(c);
                }
            } catch (err) {
                if (!cancelled) setError(err.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        run();
        return () => { cancelled = true; };
    }, [filters]);

    const sortedRows = useMemo(() => {
        const order = { high: 0, medium: 1, low: 2 };
        return [...rows].sort((a, b) => {
            const s = (order[a.severity] ?? 9) - (order[b.severity] ?? 9);
            if (s !== 0) return s;
            return new Date(b.created_at_utc) - new Date(a.created_at_utc);
        });
    }, [rows]);

    return (
        <div style={styles.page}>
            <div>
                <h1 style={styles.h1}>Anomaly flags</h1>
                <div style={styles.sub}>Deterministic rule hits across P-card, fuel-card, and HCSS timecards.</div>
            </div>

            <div style={styles.cards}>
                <Card label="Open · high"   value={counts?.open?.high ?? '—'} />
                <Card label="Open · medium" value={counts?.open?.medium ?? '—'} />
                <Card label="Open · low"    value={counts?.open?.low ?? '—'} />
                <Card label="Resolved"      value={counts?.resolved ?? '—'} />
                <Card label="Escalated"     value={counts?.escalated ?? '—'} />
            </div>

            <Filters filters={filters} setFilters={setFilters} />

            {error && <div style={styles.err}>Error: {error}</div>}

            <table style={styles.table}>
                <thead>
                    <tr>
                        <th style={styles.th}>Severity</th>
                        <th style={styles.th}>Rule</th>
                        <th style={styles.th}>Source</th>
                        <th style={styles.th}>Employee</th>
                        <th style={styles.th}>Project</th>
                        <th style={styles.th}>Amount</th>
                        <th style={styles.th}>Reason</th>
                        <th style={styles.th}>Created</th>
                        <th style={styles.th}></th>
                    </tr>
                </thead>
                <tbody>
                    {loading && (
                        <tr><td colSpan={9} style={styles.empty}>Loading…</td></tr>
                    )}
                    {!loading && sortedRows.length === 0 && (
                        <tr><td colSpan={9} style={styles.empty}>No flags match the current filters.</td></tr>
                    )}
                    {!loading && sortedRows.map(f => (
                        <tr key={f.id}>
                            <td style={styles.td}><span style={styles.pill(f.severity)}>{f.severity.toUpperCase()}</span></td>
                            <td style={styles.td}><code style={{ fontSize: 12 }}>{f.rule_id}</code></td>
                            <td style={styles.td}>{f.source_type}</td>
                            <td style={styles.td}>{f.cardholder_or_employee_id || '—'}</td>
                            <td style={styles.td}>{f.project_id || '—'}</td>
                            <td style={styles.td}>{f.flag_amount_usd != null ? `$${Number(f.flag_amount_usd).toFixed(2)}` : '—'}</td>
                            <td style={styles.td}>{f.computed_reason}</td>
                            <td style={styles.td}>{f.created_at_utc ? new Date(f.created_at_utc).toLocaleString() : '—'}</td>
                            <td style={styles.td}>
                                <Link to={`/anomaly/flags/${encodeURIComponent(f.id)}`} style={styles.link}>open</Link>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function Card({ label, value }) {
    return (
        <div style={styles.card}>
            <div style={styles.cardLbl}>{label}</div>
            <div style={styles.cardVal}>{value}</div>
        </div>
    );
}

function Filters({ filters, setFilters }) {
    return (
        <div style={styles.bar}>
            <Group label="Severity">
                {SEVERITIES.map(s => (
                    <span key={s}
                          onClick={() => setFilters({ ...filters, severity: s })}
                          style={{ ...styles.chip, ...(filters.severity === s ? styles.chipOn : {}) }}>
                        {s}
                    </span>
                ))}
            </Group>
            <Group label="Status">
                {STATUSES.map(s => (
                    <span key={s}
                          onClick={() => setFilters({ ...filters, status: s })}
                          style={{ ...styles.chip, ...(filters.status === s ? styles.chipOn : {}) }}>
                        {s}
                    </span>
                ))}
            </Group>
            <Group label="Source">
                {SOURCES.map(s => (
                    <span key={s}
                          onClick={() => setFilters({ ...filters, source_type: s })}
                          style={{ ...styles.chip, ...(filters.source_type === s ? styles.chipOn : {}) }}>
                        {s}
                    </span>
                ))}
            </Group>
        </div>
    );
}

function Group({ label, children }) {
    return (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</span>
            {children}
        </div>
    );
}
